<template>
  <div class="process-picker" data-testid="process-picker">
    <div class="process-picker__title" data-testid="process-picker-title">
      Select a decoration process
    </div>
    <div
      v-if="loading"
      class="process-picker__loading"
      data-testid="process-picker-loading"
    >
      Loading processes...
    </div>
    <div
      v-else-if="loadError"
      class="process-picker__error"
      data-testid="process-picker-error"
    >
      {{ loadError }}
    </div>
    <div
      v-else-if="processes.length === 0"
      class="process-picker__empty"
      data-testid="process-picker-empty"
    >
      No decoration processes are available. Contact platform support.
    </div>
    <div v-else class="process-picker__body">
      <!--
        Method<->format lock (PR-5 / WA-7c): when ``lockedMethodSlug``
        is set (a .dst/.emb artwork is an embroidery stitch source),
        the matching option is auto-selected and every other option is
        disabled. The marker lets tests + the ui-runner assert the lock
        is active. The backend guard (handle_composite_bind) is the
        authority; this is UX so the user can't pick an impossible
        method. The radio list still renders while locked (embroidery
        checked, others disabled) so the choice is visible, not hidden.
      -->
      <p
        v-if="isLocked"
        class="process-picker__lock-note"
        :data-testid="`method-locked-${lockedMethodSlug}`"
      >
        This is an embroidery file, so the method is set to embroidery.
      </p>
      <div
        role="radiogroup"
        aria-label="Decoration process"
        class="process-picker__list"
        data-testid="process-picker-list"
      >
      <label
        v-for="process in processes"
        :key="process.id"
        :class="[
          'process-picker__option',
          { 'process-picker__option--selected': modelValue === process.id },
          { 'process-picker__option--disabled': isDisabled(process) }
        ]"
        :data-testid="`process-picker-option-${process.slug}`"
      >
        <input
          type="radio"
          name="process-picker-process"
          :value="process.id"
          :checked="modelValue === process.id"
          :disabled="isDisabled(process)"
          class="process-picker__radio"
          :data-testid="`method-${process.slug}`"
          @change="onSelect(process.id)"
        />
        <span class="process-picker__option-body">
          <span
            class="process-picker__option-label"
            :data-testid="`process-picker-label-${process.slug}`"
          >
            {{ process.display_name }}
          </span>
          <span
            v-if="describe(process.slug)"
            class="process-picker__option-sub"
            :data-testid="`process-picker-sub-${process.slug}`"
          >
            {{ describe(process.slug) }}
          </span>
        </span>
      </label>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "@/axios";

/**
 * ProcessPicker.vue - shared decoration-process picker (ADR 0045).
 *
 * Card: 2026-06-13 design bundle implementation -- https://trello.com/c/hYlwyKYT
 * Pebble: Phase 3.6 wizard audit + updates.
 * ADR: ADR 0045 (process selection at creation).
 * Audit: planning/design-handoffs/handoff_2026-06-13/implementation-plan/
 *        WIZARD-FUNCTIONALITY-AUDIT.md
 *
 * Renders the four-row decoration_processes lookup as a radio-card
 * list. Per ADR 0045, process lives on the artwork (not the placement)
 * and is chosen at creation. The same picker mounts in two places:
 *
 *   1. DecorationAddWizard (Step 2) -- new artwork upload from the
 *      Decoration Library shell.
 *   2. Product editor's repository picker "Upload new artwork"
 *      sub-flow (future PR; not part of Phase 3.6 scope).
 *
 * Data source: GET /api/decoration_processes/ -- the read surface
 * landed in Phase 3.6 alongside this component (see api/src/endpoints/
 * artworks.py::get_decoration_processes). The endpoint filters by
 * is_active=true so soft-disabled methods do not surface.
 *
 * v-model contract: emits ``update:modelValue`` with the selected
 * process's ``id`` (int). Parents bind via v-model in Vue 3 SFC
 * templates or :model-value + @update:model-value in script setup.
 *
 * Per-slug sub-line copy lives client-side because the design-source
 * sub-lines (ADR 0045) are stable marketing copy, not data. If the
 * platform later adds a new process (e.g. ``sublimation``) without
 * client code update, the picker still renders the row -- it just
 * has no sub-line.
 */

// Sub-line copy keyed by decoration_processes.slug. Sourced from ADR
// 0045. Keep in sync if ADR 0045 is updated.
const PROCESS_SUBLINES = {
  embroidery: "Stitch files generated by decorator. 1-3 day digitization.",
  screen_print: "Color-separated films. 1-2 day prep.",
  dtf: "Full-color transfer. Fastest digitization.",
  htv: "Solid-color vinyl cut to shape.",
};

export default {
  name: "ProcessPicker",
  props: {
    modelValue: {
      type: [Number, String, null],
      default: null,
    },
    // PR-5 / WA-7c method<->format lock. When set to a process slug
    // (e.g. "embroidery" for a .dst/.emb artwork), the picker
    // auto-selects the matching process and disables every other
    // option. null / "" leaves the picker unconstrained. Keyed by SLUG
    // (not id) because decoration_processes ids drift across DBs.
    lockedMethodSlug: {
      type: [String, null],
      default: null,
    },
  },
  emits: ["update:modelValue", "loaded", "load-error"],
  data() {
    return {
      processes: [],
      loading: true,
      loadError: null,
    };
  },
  computed: {
    isLocked() {
      return Boolean(this.lockedMethodSlug);
    },
    lockedProcess() {
      if (!this.isLocked) return null;
      return (
        this.processes.find((p) => p.slug === this.lockedMethodSlug) || null
      );
    },
  },
  watch: {
    // Re-apply the lock whenever it changes OR the process list arrives
    // after the lock was set (mount order is not guaranteed).
    lockedMethodSlug() {
      this.applyLock();
    },
    processes() {
      this.applyLock();
    },
  },
  async mounted() {
    await this.fetchProcesses();
  },
  methods: {
    describe(slug) {
      return PROCESS_SUBLINES[slug] || "";
    },
    isDisabled(process) {
      // When locked, every option EXCEPT the locked one is disabled.
      return this.isLocked && process.slug !== this.lockedMethodSlug;
    },
    applyLock() {
      // Auto-select the locked process once it is resolvable from the
      // loaded list. Guard the emit so we do not fire on every re-render
      // when the value is already correct.
      const locked = this.lockedProcess;
      if (locked && this.modelValue !== locked.id) {
        this.$emit("update:modelValue", locked.id);
      }
    },
    onSelect(id) {
      this.$emit("update:modelValue", id);
    },
    async fetchProcesses() {
      this.loading = true;
      this.loadError = null;
      try {
        const resp = await axios.get("/api/decoration_processes/");
        const rows = (resp && resp.data && resp.data.decoration_processes) || [];
        this.processes = rows;
        this.$emit("loaded", rows);
      } catch (err) {
        this.loadError =
          (err && err.message) ||
          "Could not load decoration processes.";
        this.processes = [];
        this.$emit("load-error", err);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.process-picker {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.process-picker__title {
  font-size: 14px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.8);
}
.process-picker__loading,
.process-picker__empty {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.6);
  padding: 12px 0;
}
.process-picker__error {
  font-size: 13px;
  color: #d32f2f;
  padding: 12px 0;
}
.process-picker__body {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.process-picker__lock-note {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.7);
  background: rgba(25, 118, 210, 0.06);
  border: 1px solid rgba(25, 118, 210, 0.25);
  border-radius: 6px;
  padding: 8px 12px;
  margin: 0;
}
.process-picker__list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.process-picker__option--disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.process-picker__option--disabled:hover {
  border-color: rgba(0, 0, 0, 0.18);
}
.process-picker__option {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 14px;
  border: 1px solid rgba(0, 0, 0, 0.18);
  border-radius: 6px;
  cursor: pointer;
  transition: border-color 120ms ease, background-color 120ms ease;
}
.process-picker__option:hover {
  border-color: rgba(25, 118, 210, 0.4);
}
.process-picker__option--selected {
  border-color: #1976d2;
  background-color: rgba(25, 118, 210, 0.06);
}
.process-picker__radio {
  margin-top: 3px;
}
.process-picker__option-body {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.process-picker__option-label {
  font-size: 14px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.85);
}
.process-picker__option-sub {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
}
</style>
